import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PortalIssue } from '@raptor/shared';
import { ApiError } from '@/lib/api/client';
import { useApiClient } from '@/lib/api/useApiClient';
import { useAuth } from '@/lib/auth/useAuth';
import { xhrUpload } from '@/features/uploads/lib/xhrUpload';
import type {
  AddCommentInput,
  AttachmentUploadResponse,
  Comment,
  CreateIssueInput,
  IssueDetailResponse,
  IssueListFilters,
  IssueListResponse,
  ProductListResponse,
} from './types';

const issueKeys = {
  all: ['issues'] as const,
  list: (filters: IssueListFilters) => [...issueKeys.all, 'list', filters] as const,
  detail: (id: string) => [...issueKeys.all, id] as const,
};

const productKeys = { all: ['products'] as const };

function buildQuery(filters: IssueListFilters): Record<string, string | number | undefined> {
  return {
    status: filters.status,
    issueType: filters.issueType,
    limit: filters.limit,
    offset: filters.offset,
    customerId: filters.customerId,
  };
}

export function useIssues(filters: IssueListFilters) {
  const api = useApiClient();
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: issueKeys.list(filters),
    enabled: Boolean(accessToken),
    queryFn: () =>
      api.request<IssueListResponse>('/issues', {
        query: buildQuery(filters),
      }),
  });
}

export function useIssue(id: string | undefined) {
  const api = useApiClient();
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: issueKeys.detail(id ?? ''),
    enabled: Boolean(id) && Boolean(accessToken),
    queryFn: () => api.request<IssueDetailResponse>(`/issues/${id}`),
  });
}

export function useProducts() {
  const api = useApiClient();
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: productKeys.all,
    enabled: Boolean(accessToken),
    // Config-style data — won't change during a session.
    staleTime: Infinity,
    queryFn: () => api.request<ProductListResponse>('/products'),
  });
}

export function useCreateIssue() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateIssueInput) =>
      api.request<PortalIssue>('/issues', { method: 'POST', body: input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: issueKeys.all });
    },
  });
}

export function useAddComment(issueId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AddCommentInput) =>
      api.request<Comment>(`/issues/${issueId}/comments`, { method: 'POST', body: input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: issueKeys.detail(issueId) });
    },
  });
}

export interface UploadAttachmentParams {
  file: File;
  token: string | undefined;
  onProgress?: (loaded: number, total: number) => void;
  signal?: AbortSignal;
}

// POST /issues/attachments — multipart upload driven by XHR (fetch still can't
// stream upload progress in browsers). Kept as a plain helper rather than a
// mutation because the picker manages its own per-file lifecycle and doesn't
// benefit from React Query's shared cache.
export async function uploadAttachment(
  params: UploadAttachmentParams,
): Promise<AttachmentUploadResponse> {
  const { file, token, onProgress, signal } = params;
  const base = import.meta.env.VITE_API_URL as string;
  const url = `${base.replace(/\/+$/, '')}/issues/attachments`;

  const form = new FormData();
  form.append('file', file, file.name);

  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  const result = await xhrUpload({
    url,
    method: 'POST',
    body: form,
    headers,
    onProgress,
    signal,
  });

  const text = result.body;
  const parsed = text ? safeJson(text) : null;

  if (result.status < 200 || result.status >= 300) {
    const payload = (parsed ?? {}) as { message?: string; code?: string; details?: unknown };
    throw new ApiError(
      payload.message ?? `Upload failed (HTTP ${result.status})`,
      result.status,
      payload.code,
      payload.details,
    );
  }

  if (!parsed) {
    throw new ApiError('Attachment upload returned an empty body', result.status);
  }
  return parsed as AttachmentUploadResponse;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}
